import React from 'react';
import Section from '../components/common/Section';
import { useToastStore } from '../store/toastStore';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { buildMiniEsgAuthHeaders, getMiniEsgApiBase } from '../utils/miniEsgApi';

type Connector = {
  id: string;
  name: string;
  vendor: string;
  category: string;
  status: 'draft' | 'testing' | 'approved';
  description?: string;
  contactEmail?: string;
  createdAt: number;
  updatedAt: number;
};

const STATUS_BADGES: Record<string, string> = {
  draft: 'bg-neutral-800 text-neutral-300',
  testing: 'bg-yellow-900/40 text-yellow-200',
  approved: 'bg-green-900/40 text-green-200',
};

export function ConnectorRegistry() {
  const API_BASE = React.useMemo(() => getMiniEsgApiBase(), []);
  const { showToast } = useToastStore();
  const [connectors, setConnectors] = React.useState<Connector[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [filter, setFilter] = React.useState<'all' | 'draft' | 'testing' | 'approved'>('all');
  const [form, setForm] = React.useState({
    name: '',
    vendor: '',
    category: 'general',
    description: '',
    contactEmail: '',
  });

  const fetchConnectors = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      const response = await fetch(`${API_BASE}/connectors${params.toString() ? `?${params}` : ''}`, {
        headers: buildMiniEsgAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to load connectors');
      const data = await response.json();
      setConnectors(data?.connectors || []);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to load connectors', 'error');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, filter, showToast]);

  React.useEffect(() => {
    fetchConnectors();
  }, [fetchConnectors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/connectors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...buildMiniEsgAuthHeaders() },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error('Submission failed');
      showToast('Connector submitted', 'success');
      setForm({ name: '', vendor: '', category: 'general', description: '', contactEmail: '' });
      fetchConnectors();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Submission failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section title="Connector Registry">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-neutral-800 rounded-lg p-4 bg-black/40 space-y-4">
          <div>
            <h3 className="text-base font-orbitron text-evat-neon mb-2">Submit New Connector</h3>
            <p className="text-sm text-neutral-400">Provide details about your connector to start review.</p>
          </div>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Vendor</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100"
                value={form.vendor}
                onChange={(e) => setForm((prev) => ({ ...prev, vendor: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Category</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100"
                value={form.category}
                onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Contact Email</label>
              <input
                type="email"
                className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100"
                value={form.contactEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="team@example.com"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-500 mb-1">Description</label>
              <textarea
                className="w-full px-3 py-2 bg-black border border-neutral-700 rounded text-sm text-neutral-100 min-h-[90px]"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-evat-neon/20 border border-evat-neon text-evat-neon rounded text-sm font-orbitron disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit Connector'}
            </button>
          </form>
        </div>

        <div className="border border-neutral-800 rounded-lg p-4 bg-black/40">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-base font-orbitron text-evat-gold">Connector Catalog</h3>
              <p className="text-xs text-neutral-500">Track submissions and approval status.</p>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="px-3 py-1 text-sm bg-black border border-neutral-700 rounded text-neutral-100"
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="testing">Testing</option>
              <option value="approved">Approved</option>
            </select>
          </div>
          {loading ? (
            <LoadingSpinner />
          ) : connectors.length === 0 ? (
            <div className="text-sm text-neutral-500 border border-dashed border-neutral-700 rounded p-3">
              No connectors found for this filter.
            </div>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {connectors.map((connector) => (
                <div key={connector.id} className="border border-neutral-800 rounded p-3 bg-black/60">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-neutral-100">{connector.name}</div>
                      <div className="text-xs text-neutral-500">{connector.vendor} • {connector.category}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGES[connector.status]}`}>
                      {connector.status}
                    </span>
                  </div>
                  {connector.description && (
                    <p className="text-xs text-neutral-400 mt-2">{connector.description}</p>
                  )}
                  <div className="text-[11px] text-neutral-500 mt-2 flex justify-between">
                    <span>Created: {new Date(connector.createdAt).toLocaleDateString()}</span>
                    {connector.contactEmail && <span>{connector.contactEmail}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
          <button
            onClick={fetchConnectors}
            className="mt-3 text-xs text-neutral-400 underline"
          >
            Refresh list
          </button>
        </div>
      </div>
    </Section>
  );
}

export default ConnectorRegistry;

